var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { TextareaAutosize, IconButton, Popover, Stack } from '@mui/material';
import EmojiPicker, { EmojiStyle } from 'emoji-picker-react';
import emojiIcon from '../../../assets/images/emojiIcon.svg';
import attachIcon from '../../../assets/images/attachIcon.svg';
import { DEFAULT_BACKGROUND_COLOR, MAX_FILES_SIZE_BYTES } from '../../../common';
import { WidgetContext } from '../../App';
import { SubmitIcon } from './SubmitIcon';
import { SizeExceededModal } from './SizeExceededModal';
import { Replay } from './Replay';
import * as styles from './ChatInput.module.css';
import { UploadedFilePreview } from './UploadedFilePreview';
export const ChatInput = ({ replayMessage, removeReplay, }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const intl = useIntl();
    const { widgetSettings, socketService, api } = useContext(WidgetContext);
    const [inputAnchor, setInputAnchor] = useState(null);
    const [message, setMessage] = useState('');
    const [isSizeExceeded, setIsSizeExceeded] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(null);
    const textareaRef = useRef(null);
    useEffect(() => {
        var _a;
        (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [replayMessage]);
    useEffect(() => {
        const textarea = textareaRef.current;
        if (cursorPosition !== null && textarea) {
            textarea.selectionStart = cursorPosition;
            textarea.selectionEnd = cursorPosition;
            setCursorPosition(null);
        }
    }, [cursorPosition, message]);
    const handleEmojiClick = (event) => {
        setInputAnchor(inputAnchor ? null : event.currentTarget);
    };
    const insertEmojiAtCursor = (emoji) => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const updatedMessage = message.slice(0, start) + emoji + message.slice(end);
        setMessage(updatedMessage);
        setCursorPosition(start + emoji.length);
    };
    const uploadFiles = (files) => __awaiter(void 0, void 0, void 0, function* () {
        const filesSize = files.reduce((totalSize, file) => totalSize + file.size, 0);
        if (filesSize > MAX_FILES_SIZE_BYTES) {
            setIsSizeExceeded(true);
            return;
        }
        // @ts-expect-error ' '
        const uploadedFiles = yield api.sendFiles(files);
        // @ts-expect-error ' '
        setUploadedFiles((files_) => [...files_, ...uploadedFiles.map((file, i) => (Object.assign(Object.assign({}, file), { type: files[i].type })))]);
        // removeReplay();
    });
    const onSendMessage = () => {
        if ((message.trim() === '' && uploadedFiles.length === 0) || !socketService)
            return;
        socketService.sendMessageEmit(Object.assign({ text: message, replyToId: replayMessage === null || replayMessage === void 0 ? void 0 : replayMessage.id }, (uploadedFiles.length === 0 ? {} : { files: uploadedFiles })));
        setMessage('');
        removeReplay();
        setUploadedFiles([]);
    };
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSendMessage();
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.inputContainer, children: [_jsx(Replay, { replayMessage: replayMessage, color: (widgetSettings === null || widgetSettings === void 0 ? void 0 : widgetSettings.backgroundColor) || DEFAULT_BACKGROUND_COLOR, removeReplay: removeReplay }), _jsxs(Stack, { className: styles.inputWrapper, sx: () => ({ flexDirection: 'row', overflow: 'auto', p: '8px 8px 8px 20px' }), children: [_jsx(TextareaAutosize, { onPaste: (event) => __awaiter(void 0, void 0, void 0, function* () {
                                    const files = [...event.clipboardData.items]
                                        .map((item) => item.getAsFile())
                                        .filter((file) => file !== null);
                                    if (files.length === 0)
                                        return;
                                    uploadFiles(files);
                                }), autoFocus: true, maxRows: 6, value: message, onKeyDown: onKeyDown, onChange: (e) => setMessage(e.target.value), placeholder: intl.formatMessage({ id: 'chatInputPlaceholder' }), className: styles.textareaInput, ref: textareaRef }), _jsx(IconButton, { "aria-label": 'emoji', color: 'inherit', onClick: handleEmojiClick, children: _jsx("img", { src: emojiIcon, alt: 'choose emoji', draggable: 'false' }) }), _jsxs(IconButton, { "aria-label": 'file-upload', color: 'inherit', component: 'label', children: [_jsx("input", { hidden: true, type: 'file', id: 'file-upload', onChange: (event) => {
                                            var _a;
                                            const files = [...((_a = event.target.files) !== null && _a !== void 0 ? _a : [])];
                                            uploadFiles(files);
                                        }, multiple: true }), _jsx("img", { src: attachIcon, alt: 'attach file', draggable: 'false' })] }), (message.trim() !== '' || uploadedFiles.length > 0) && (_jsx(IconButton, { "aria-label": 'submit', color: 'inherit', onClick: onSendMessage, children: _jsx(SubmitIcon, { color: (widgetSettings === null || widgetSettings === void 0 ? void 0 : widgetSettings.backgroundColor) || DEFAULT_BACKGROUND_COLOR }) }))] }), uploadedFiles.length > 0 && (_jsx(Stack, { sx: () => ({ flexDirection: 'row', flexWrap: 'wrap', gap: '16px', p: '16px' }), children: uploadedFiles.map((uploadedFile) => {
                            var _a;
                            return (_jsx(UploadedFilePreview, { link: `${uploadedFile.link}?clientId=${(_a = socketService === null || socketService === void 0 ? void 0 : socketService.joinResponseData) === null || _a === void 0 ? void 0 : _a.client_id}`, type: uploadedFile.type, file: uploadedFile, onDeleteFile: (uploadedFileId) => {
                                    setUploadedFiles((uploadedFiles) => uploadedFiles.filter(({ id }) => id !== uploadedFileId));
                                }, sx: () => ({ height: '56px' }) }, uploadedFile.id));
                        }) }))] }), _jsx(Popover, { open: Boolean(inputAnchor), anchorEl: inputAnchor, onClose: handleEmojiClick, anchorOrigin: { vertical: 'top', horizontal: 'left' }, transformOrigin: { vertical: 'bottom', horizontal: 'left' }, children: _jsx(EmojiPicker, { emojiStyle: EmojiStyle.NATIVE, previewConfig: { showPreview: false }, emojiVersion: '4.0', height: '65vh', width: '325px', onEmojiClick: (emoji) => insertEmojiAtCursor(emoji.emoji) }) }), _jsx(SizeExceededModal, { isOpen: isSizeExceeded, buttonColor: (widgetSettings === null || widgetSettings === void 0 ? void 0 : widgetSettings.backgroundColor) || DEFAULT_BACKGROUND_COLOR, setIsOpen: setIsSizeExceeded })] }));
};
